import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, throwError, of, forkJoin } from 'rxjs';
import { DashboardStats, CalendarEvent } from '../models';
import { APP_CONSTANTS } from '../constants/app.constants';
import { ErrorHandlerService } from './error-handler.service';
import { MOCK_DASHBOARD_STATS, MOCK_DASHBOARD_CALENDAR_EVENTS } from '../mock-data';
import { AuthService } from './auth.service';
import { AdminService } from './admin.service';
import { PropertyService } from './property.service';
import { PermissionService, PermissionDocument } from './permission.service';
import { ProfileService } from './profile.service';
import { VendorService } from './vendor.service';
import { CollectionTables } from '../models/CollectionTables.model';
import { Firestore, collection, collectionGroup, query, where, getDocs, getDoc, doc } from '@angular/fire/firestore';
import { firstValueFrom, from } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private http = inject(HttpClient);
  private errorHandler = inject(ErrorHandlerService);
  private firestore = inject(Firestore);
  private authService = inject(AuthService);
  private adminService = inject(AdminService);
  private propertyService = inject(PropertyService);
  private permissionService = inject(PermissionService);
  private profileService = inject(ProfileService);
  private vendorService = inject(VendorService);
  private readonly apiUrl = APP_CONSTANTS.API.BASE_URL;

  /**
   * Get dashboard statistics from Firebase
   * Gets counts for admins, properties, residents, vendors, amenity bookings, and pet registries
   */
  getDashboardStats(): Observable<DashboardStats> {
    return from(
      (async () => {
        try {
          const currentUser = this.authService.getCurrentUser();
          if (!currentUser) {
            console.error('❌ No current user');
            return MOCK_DASHBOARD_STATS;
          }

          const administratorId = await this.authService.getAdministratorId(currentUser.uid);
          if (!administratorId) {
            console.error('❌ No administrator ID');
            return MOCK_DASHBOARD_STATS;
          }

          console.log('📊 Getting dashboard stats for administrator:', administratorId);

          const employee = await this.authService.getCurrentEmployee(currentUser.uid);

          // Get counts from each collection (property count matches /app/properties — permission scope)
          const [admins, properties, residents, vendors, amenityBookings, petRegistries] = await Promise.all([
            this.getAdminsCount(administratorId),
            this.getPropertiesCountForUser(administratorId, employee),
            this.getResidentsCount(administratorId),
            this.getVendorsCount(administratorId),
            this.getAmenityBookingsCount(administratorId),
            this.getPetRegistriesCount(administratorId),
          ]);

          const stats: DashboardStats = {
            admins,
            properties,
            residents,
            vendors,
            amenityBookings,
            petRegistries,
          };

          console.log('✅ Dashboard stats retrieved:', stats);
          return stats;
        } catch (error) {
          console.error('❌ Error getting dashboard stats:', error);
          return MOCK_DASHBOARD_STATS;
        }
      })()
    );
  }

  /**
   * Get count of all employees for an administrator (managers, admins, clerks)
   */
  private async getAdminsCount(businessId: string): Promise<number> {
    try {
      // Try businesses collection first, fall back to legacy administrators
      const inBusinesses = (await getDoc(doc(this.firestore, 'businesses', businessId))).exists();
      const collName = inBusinesses ? 'businesses' : 'administrators';
      const employeesRef = collection(this.firestore, collName, businessId, 'employees');
      const snapshot = await getDocs(query(employeesRef));
      return snapshot.size;
    } catch (error) {
      console.error('Error getting employees count:', error);
      return 0;
    }
  }

  /**
   * Count distinct property IDs from permission rows (same uniqueness as UserContextService).
   */
  private countDistinctPropertyIds(permissions: PermissionDocument[]): number {
    const seen = new Set<string>();
    for (const p of permissions) {
      if (p.propertyId) {
        seen.add(p.propertyId);
      }
    }
    return seen.size;
  }

  /**
   * Properties shown on /app/properties come from UserContextService.permittedProperties.
   * This count uses the same rules so the dashboard card matches that list.
   * Uses root `properties` collection counts only (not collectionGroup) to avoid nested
   * subcollections also named `properties` inflating the number.
   */
  private async getRootPropertiesCount(businessId: string): Promise<number> {
    try {
      // Query by new businessId field; fall back to legacy administratorId for old data
      let propertiesQuery = query(
        collection(this.firestore, CollectionTables.properties),
        where('businessId', '==', businessId)
      );
      let snapshot = await getDocs(propertiesQuery);
      // Backward compat: if no results, try legacy administratorId field
      if (snapshot.empty) {
        propertiesQuery = query(
          collection(this.firestore, CollectionTables.properties),
          where('administratorId', '==', businessId)
        );
        snapshot = await getDocs(propertiesQuery);
      }
      return snapshot.size;
    } catch (error) {
      console.error('Error getting root properties count:', error);
      return 0;
    }
  }

  private async getPropertiesCountForUser(
    administratorId: string,
    employee: { id: string; role: string; administratorId: string } | null
  ): Promise<number> {
    try {
      if (!employee) {
        return 0;
      }

      const roleNorm = (employee.role || 'clerk').toLowerCase();

      if (roleNorm === 'manager') {
        const permRows = await firstValueFrom(
          this.permissionService.getByAdministratorId(administratorId)
        );
        const fromPerms = this.countDistinctPropertyIds(permRows);
        if (fromPerms === 0 && administratorId) {
          return await this.getRootPropertiesCount(administratorId);
        }
        return fromPerms;
      }

      if (
        roleNorm === 'admin' ||
        roleNorm === 'clerk' ||
        roleNorm === 'maintenance' ||
        roleNorm === 'agent'
      ) {
        const perms = await firstValueFrom(
          this.permissionService.getByEmployeeId(employee.id)
        );
        return this.countDistinctPropertyIds(perms);
      }

      return await this.getRootPropertiesCount(administratorId);
    } catch (error) {
      console.error('Error getting properties count for user:', error);
      return 0;
    }
  }

  /**
   * Get count of residents for an administrator
   */
  private async getResidentsCount(businessId: string): Promise<number> {
    try {
      return await firstValueFrom(
        this.profileService.countNonApplicantProfilesForBusiness(businessId)
      );
    } catch (error) {
      console.error('Error getting residents count:', error);
      return 0;
    }
  }

  /**
   * Get count of vendors for a business.
   * Counts vendors in businesses/{businessId}/vendors subcollection.
   */
  private async getVendorsCount(businessId: string): Promise<number> {
    try {
      // Try businesses collection first, fall back to legacy administrators
      const inBusinesses = (await getDoc(doc(this.firestore, 'businesses', businessId))).exists();
      const collName = inBusinesses ? 'businesses' : 'administrators';
      const vendorsRef = collection(this.firestore, collName, businessId, 'vendors');
      const snapshot = await getDocs(vendorsRef);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting vendors count:', error);
      return 0;
    }
  }

  /**
   * Get count of amenity bookings for an administrator
   */
  private async getAmenityBookingsCount(businessId: string): Promise<number> {
    try {
      const bookingsQuery = collectionGroup(this.firestore, 'amenity_bookings');
      const snapshot = await getDocs(bookingsQuery);

      const count = snapshot.docs.filter(doc => {
        const data = doc.data();
        return data['businessId'] === businessId || data['administratorId'] === businessId;
      }).length;
      
      return count;
    } catch (error) {
      console.error('Error getting amenity bookings count:', error);
      return 0;
    }
  }

  /**
   * Get count of pet registries for an administrator
   */
  private async getPetRegistriesCount(businessId: string): Promise<number> {
    try {
      const petsQuery = collectionGroup(this.firestore, 'pets');
      const snapshot = await getDocs(petsQuery);

      const count = snapshot.docs.filter(doc => {
        const data = doc.data();
        return data['businessId'] === businessId || data['administratorId'] === businessId;
      }).length;
      
      return count;
    } catch (error) {
      console.error('Error getting pet registries count:', error);
      return 0;
    }
  }

  getCalendarEvents(): Observable<CalendarEvent[]> {
    return of(MOCK_DASHBOARD_CALENDAR_EVENTS);
  }

  getEventsByDate(date: Date): Observable<CalendarEvent[]> {
    return this.getCalendarEvents().pipe(
      map((events) => events.filter((event) => event.date.toDateString() === date.toDateString()))
    );
  }

  createCalendarEvent(event: CalendarEvent): Observable<CalendarEvent> {
    return this.http
      .post<CalendarEvent>(`${this.apiUrl}/${APP_CONSTANTS.API.ENDPOINTS.CALENDAR_EVENTS}`, event)
      .pipe(
        catchError((error) => {
          this.errorHandler.handleError(error, 'فشل في إنشاء حدث جديد');
          return throwError(() => error);
        })
      );
  }

  updateCalendarEvent(id: string, event: CalendarEvent): Observable<CalendarEvent> {
    return this.http
      .put<CalendarEvent>(
        `${this.apiUrl}/${APP_CONSTANTS.API.ENDPOINTS.CALENDAR_EVENTS}/${id}`,
        event
      )
      .pipe(
        catchError((error) => {
          this.errorHandler.handleError(error, 'فشل في تحديث الحدث');
          return throwError(() => error);
        })
      );
  }

  deleteCalendarEvent(id: string): Observable<void> {
    return this.http
      .delete<void>(`${this.apiUrl}/${APP_CONSTANTS.API.ENDPOINTS.CALENDAR_EVENTS}/${id}`)
      .pipe(
        catchError((error) => {
          this.errorHandler.handleError(error, 'فشل في حذف الحدث');
          return throwError(() => error);
        })
      );
  }
}
